import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Grid,
  InputAdornment,
  CircularProgress,
  Alert,
  Container,
  Paper,
  Avatar,
  Select,
  MenuItem
} from '@mui/material';

import {
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  School as TutorIcon,
  Language as LanguageIcon
} from '@mui/icons-material';

import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/* ---------- Language Translations ---------- */

const translations = {
  en: {
    title: "Tutor Registration",
    name: "Full Name",
    email: "Email Address",
    phone: "Phone Number",
    password: "Password",
    confirmPassword: "Confirm Password",
    register: "Register",
    login: "Already have an account? Sign in"
  },

  yo: {
    title: "Ifọwọsi Olukọ",
    name: "Orukọ Kikun",
    email: "Adirẹsi Imeeli",
    phone: "Nọmba Foonu",
    password: "Ọrọigbaniwọle",
    confirmPassword: "Jẹrisi Ọrọigbaniwọle",
    register: "Forukọsilẹ",
    login: "Ṣe o ni akọọlẹ tẹlẹ? Wọle"
  },

  ig: {
    title: "Ndebanye aha Onye Nkuzi",
    name: "Aha zuru ezu",
    email: "Adreesị Email",
    phone: "Nọmba ekwentị",
    password: "Okwuntughe",
    confirmPassword: "Kwenye Okwuntughe",
    register: "Debanye aha",
    login: "Ị nwere akaụntụ? Banye"
  },

  ha: {
    title: "Rajistar Malami",
    name: "Cikakken suna",
    email: "Adireshin Imel",
    phone: "Lambar waya",
    password: "Kalmar sirri",
    confirmPassword: "Tabbatar da Kalmar sirri",
    register: "Yi Rajista",
    login: "Kana da asusu? Shiga"
  }
};

export default function Register() {

  const [language, setLanguage] = useState('en');
  const t = translations[language];

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  /* -------- DO NOT TOUCH API STRUCTURE (UNCHANGED) -------- */

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {

      const response = await axios.post(`${BASE_URL}/api/auth/tutors/register`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password
      });

      console.log('Registration successful:', response.data);

      setSuccess(true);

      setTimeout(() => navigate('/tutor/login'), 2000);

    } catch (err) {

      console.error('Registration error:', err);

      const message = err.response?.data?.message || 'Registration failed. Please try again.';
      setError(message);

    } finally {

      setLoading(false);

    }
  };

  return (

    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg,#f5f7fa,#c3cfe2)",
        display: "flex",
        alignItems: "center",
        py: 4
      }}
    >

      <Container maxWidth="lg">

        <Paper
          elevation={20}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            borderRadius: "20px",
            overflow: "hidden",
            backdropFilter: "blur(10px)"
          }}
        >

          {/* ---------- IMAGE SECTION ---------- */}

          <Box
            sx={{
              flex: 1.2,
              position: "relative",
              display: { xs: "none", md: "block" }
            }}
          >

            <img
              src="/IMG_8755.jpeg"
              alt="Tutor Register"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover"
              }}
            />

            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(to bottom,transparent,rgba(0,0,0,0.7))",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                p: 4
              }}
            >

              <Typography
                variant="h3"
                sx={{ color: "#fff", fontWeight: 800 }}
              >
                Peer2Learn
              </Typography>

              <Typography sx={{ color: "#eee" }}>
                Share your knowledge and teach students worldwide
              </Typography>

            </Box>

          </Box>

          {/* ---------- FORM SECTION ---------- */}

          <Box sx={{ flex: 1, p: { xs: 4, md: 6 }, position: "relative" }}>

            {/* Language selector */}

            <Box sx={{ position: "absolute", top: 20, right: 20 }}>

              <Select
                size="small"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                IconComponent={LanguageIcon}
                variant="standard"
                disableUnderline
                sx={{
                  bgcolor: "rgba(0,0,0,0.05)",
                  px: 2,
                  borderRadius: "20px"
                }}
              >

                <MenuItem value="en">English</MenuItem>
                <MenuItem value="yo">Yoruba</MenuItem>
                <MenuItem value="ig">Igbo</MenuItem>
                <MenuItem value="ha">Hausa</MenuItem>

              </Select>

            </Box>

            <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
              <TutorIcon />
            </Avatar>

            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {t.title}
            </Typography>

            <Paper elevation={0} sx={{ mt: 3 }}>

              <Box component="form" onSubmit={handleSubmit}>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Registration successful! Redirecting to login...
                  </Alert>
                )}

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label={t.name}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon />
                      </InputAdornment>
                    )
                  }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label={t.email}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    )
                  }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label={t.phone}
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon />
                      </InputAdornment>
                    )
                  }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label={t.password}
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    )
                  }}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="confirmPassword"
                  label={t.confirmPassword}
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon />
                      </InputAdornment>
                    )
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.5,
                    borderRadius: "12px",
                    fontWeight: 600
                  }}
                >
                  {loading ? <CircularProgress size={24} /> : t.register}
                </Button>

                <Grid container justifyContent="center">
                  <Grid>
                    <Link href="/tutor/login" variant="body2">
                      {t.login}
                    </Link>
                  </Grid>
                </Grid>

              </Box>

            </Paper>

          </Box>

        </Paper>

      </Container>

    </Box>

  );
}